import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Grid, Tooltip, IconButton, Popover, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Avatar, Stack } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TagIcon from '@mui/icons-material/Tag';
import ViewCalendarIcon from '@mui/icons-material/CalendarViewMonth';
import ScheduleMeet from './ScheduleMeet';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import MeetingRoom from './MeetingRoom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const generateMeetId = () => {
    return 'meet-' + Math.random().toString(36).substring(2, 9);
};

const MeetView = ({ searchQuery = '', currentUser = null }) => {
    const [view, setView] = useState('dashboard');
    const [joinAnchorEl, setJoinAnchorEl] = useState(null);
    const [meetId, setMeetId] = useState('');
    const [meetInput, setMeetInput] = useState('');
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [scheduledMeetings, setScheduledMeetings] = useState([]);

    const getCurrentUser = () => {
        if (currentUser) return currentUser;
        try {
            const u = localStorage.getItem('x2infotech_user');
            if (u) return JSON.parse(u);
        } catch (e) {}
        return { id: Math.random().toString(36).substring(7), name: 'User' };
    };
    const activeUser = getCurrentUser();

    const filteredMeetings = scheduledMeetings.filter(meeting => 
        meeting.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        meeting.id.toLowerCase().includes(searchQuery.toLowerCase())
    );

    useEffect(() => {
        const saved = localStorage.getItem('teams_scheduled_meetings');
        if (saved) {
            try {
                setScheduledMeetings(JSON.parse(saved));
            } catch (e) {
                console.error("Failed to parse scheduled meetings", e);
            }
        }
    }, []);

    const handleJoinClick = (event) => {
        setJoinAnchorEl(event.currentTarget);
    };

    const handleJoinClose = () => {
        setJoinAnchorEl(null);
        setMeetInput('');
    };

    const handleCreateMeet = () => {
        const id = generateMeetId();
        setMeetId(id);
        setCreateDialogOpen(true);
    };

    const handleStartCreatedMeet = () => {
        setCreateDialogOpen(false);
        setView('meeting');
    };

    const handleJoinMeet = () => {
        if (meetInput.trim()) {
            setMeetId(meetInput.trim());
            handleJoinClose();
            setView('meeting');
        }
    };

    const handleSaveMeeting = (meeting) => {
        const updated = [meeting, ...scheduledMeetings];
        setScheduledMeetings(updated);
        localStorage.setItem('teams_scheduled_meetings', JSON.stringify(updated));
    };

    const openJoinPopover = Boolean(joinAnchorEl);

    if (view === 'schedule') {
        return <ScheduleMeet onBack={() => setView('dashboard')} onSave={handleSaveMeeting} />;
    }

    if (view === 'meeting' && meetId) {
        return <MeetingRoom meetId={meetId} currentUser={activeUser} onLeave={() => setView('dashboard')} />;
    }

    return (
        <Box sx={{ p: 4, flex: 1, bgcolor: '#f5f5f5', overflowY: 'auto' }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#242424', letterSpacing: '-0.5px' }}>
                    Meetings & Calls
                </Typography>
                <Typography variant="body2" sx={{ color: '#616161', mt: 0.5 }}>
                    Start instant video conferences, schedule upcoming department discussions, or join with a code.
                </Typography>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleCreateMeet}
                        startIcon={<LinkIcon />}
                        sx={{
                            py: 1.8,
                            bgcolor: '#4b53bc',
                            color: '#ffffff',
                            '&:hover': {
                                bgcolor: '#3b43a2',
                                boxShadow: '0 4px 12px rgba(75, 83, 188, 0.3)'
                            },
                            borderRadius: 1.5,
                            textTransform: 'none',
                            fontSize: '15px',
                            fontWeight: 600,
                            boxShadow: 'none'
                        }}
                    >
                        Create a meeting link
                    </Button>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => setView('schedule')}
                        startIcon={<CalendarMonthIcon sx={{ color: '#4b53bc' }} />}
                        sx={{
                            py: 1.8,
                            borderColor: '#edebe9',
                            bgcolor: '#fff',
                            color: '#242424',
                            '&:hover': { borderColor: '#4b53bc', bgcolor: '#f4f4fc' },
                            borderRadius: 1.5,
                            textTransform: 'none',
                            fontSize: '15px',
                            fontWeight: 600
                        }}
                    >
                        Schedule a meeting
                    </Button>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleJoinClick}
                        startIcon={<TagIcon sx={{ color: '#4b53bc' }} />}
                        sx={{
                            py: 1.8,
                            borderColor: '#edebe9',
                            bgcolor: '#fff',
                            color: '#242424',
                            '&:hover': { borderColor: '#4b53bc', bgcolor: '#f4f4fc' },
                            borderRadius: 1.5,
                            textTransform: 'none',
                            fontSize: '15px',
                            fontWeight: 600
                        }}
                    >
                        Join with a meeting ID
                    </Button>

                    <Popover
                        open={openJoinPopover}
                        anchorEl={joinAnchorEl}
                        onClose={handleJoinClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        PaperProps={{
                            sx: {
                                width: 320,
                                p: 3,
                                borderRadius: 2,
                                mt: 1,
                                boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                                Join a meeting with an ID
                            </Typography>
                            <IconButton size="small" onClick={handleJoinClose}>
                                <CloseIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600, mb: 0.5 }}>
                                Meeting ID <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="e.g. meet-xxxxx"
                                variant="filled"
                                value={meetInput}
                                onChange={(e) => setMeetInput(e.target.value)}
                                sx={{ '& .MuiFilledInput-root': { bgcolor: '#f5f5f5' } }}
                                InputProps={{ disableUnderline: true }}
                            />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                Type a meeting passcode
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Type a meeting passcode"
                                variant="filled"
                                sx={{ '& .MuiFilledInput-root': { bgcolor: '#f5f5f5' } }}
                                InputProps={{ disableUnderline: true }}
                            />
                        </Box>

                        <Button
                            fullWidth
                            variant="contained"
                            disabled={!meetInput.trim()}
                            onClick={handleJoinMeet}
                            sx={{
                                bgcolor: meetInput.trim() ? '#4b53bc' : '#edebe9',
                                color: meetInput.trim() ? '#fff' : '#8a8886',
                                textTransform: 'none',
                                fontWeight: 600,
                                '&.Mui-disabled': { bgcolor: '#edebe9', color: '#8a8886' },
                                '&:hover': { bgcolor: meetInput.trim() ? '#3b43a2' : '#edebe9' }
                            }}
                        >
                            Join meeting
                        </Button>
                    </Popover>
                </Grid>
            </Grid>

            <Box sx={{ mt: 6 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#242424' }}>
                    Meeting links
                </Typography>
                <Paper
                    variant="outlined"
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        borderRadius: 2,
                        borderColor: '#edebe9',
                        bgcolor: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}
                >
                    <Box sx={{
                        width: 52,
                        height: 52,
                        borderRadius: '12px',
                        bgcolor: 'rgba(75, 83, 188, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2
                    }}>
                        <LinkIcon sx={{ color: '#4b53bc', fontSize: 28 }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#242424', mb: 0.5 }}>
                        Quickly create, save, and share links with anyone.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#616161' }}>
                        All video calls and meetings support screen sharing, WebRTC real-time media, and chat.
                    </Typography>
                </Paper>
            </Box>

            <Box sx={{ mt: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#242424' }}>
                    Scheduled meetings
                </Typography>
                <Button
                    startIcon={<ViewCalendarIcon />}
                    sx={{ textTransform: 'none', color: '#616161' }}
                >
                    View in calendar
                </Button>
            </Box>

            {filteredMeetings.length === 0 ? (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        borderRadius: 2,
                        borderColor: '#edebe9',
                        bgcolor: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}
                >
                    <Typography variant="body2" color="textSecondary">
                        {searchQuery ? "No meetings found matching your search." : "No scheduled meetings yet."}
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={2}>
                    {filteredMeetings.map((meeting) => (
                        <Paper
                            key={meeting.id}
                            variant="outlined"
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                borderColor: '#edebe9',
                                bgcolor: '#fff',
                                '&:hover': { bgcolor: '#f9f9fb', borderColor: '#4b53bc' }
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Avatar sx={{ bgcolor: '#4b53bc', borderRadius: 1.5 }}>
                                        <CalendarMonthIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#242424' }}>{meeting.title}</Typography>
                                        <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#616161' }}>
                                                <AccessTimeIcon sx={{ fontSize: 16 }} />
                                                <Typography variant="caption">{meeting.startDate} • {meeting.startTime}</Typography>
                                            </Box>
                                            {meeting.location && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#616161' }}>
                                                    <LocationOnIcon sx={{ fontSize: 16 }} />
                                                    <Typography variant="caption">{meeting.location}</Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    </Box>
                                </Box>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                        setMeetId(meeting.id);
                                        setView('meeting');
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        color: '#4b53bc',
                                        borderColor: '#4b53bc',
                                        fontWeight: 600,
                                        '&:hover': { bgcolor: 'rgba(75, 83, 188, 0.08)', borderColor: '#3b43a2' }
                                    }}
                                >
                                    Join
                                </Button>
                            </Box>
                        </Paper>
                    ))}
                </Stack>
            )}

            {/* Create Meeting Dialog */}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Meeting Ready
                    <IconButton size="small" onClick={() => setCreateDialogOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, color: '#616161' }}>
                        Share this meeting code with employees or clients so they can join:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f5f5f5', p: 2, borderRadius: 1.5, border: '1px solid #edebe9' }}>
                        <Typography variant="body1" sx={{ flex: 1, fontFamily: 'monospace', fontSize: '16px', fontWeight: 700, color: '#242424' }}>
                            {meetId}
                        </Typography>
                        <Tooltip title="Copy Code">
                            <IconButton 
                                size="small" 
                                sx={{ color: '#4b53bc' }}
                                onClick={() => {
                                    navigator.clipboard.writeText(meetId);
                                    alert("Meeting code copied to clipboard!");
                                }}
                            >
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={() => setCreateDialogOpen(false)} sx={{ color: '#616161', textTransform: 'none', fontWeight: 600 }}>
                        Close
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleStartCreatedMeet}
                        sx={{
                            bgcolor: '#4b53bc',
                            color: '#fff',
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#3b43a2' }
                        }}
                    >
                        Start Meeting Now
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MeetView;
